import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import SocialAccountHandle from 'Components/social-account/SocialAccountHandle';
import { toggleSocialAccountView } from 'slices/ui/reducer';
import { AppDispatch } from 'store';
import { CollabDeletionRequest } from 'types/collaboration-deletion-request';

interface DeletionRequestsTableProps {
    requests: CollabDeletionRequest[];
    hasMore: boolean;
    loading: boolean;
    initialLoading: boolean;
    error: string | null;
    onNextPage: () => void;
}

const COLUMNS = 8;

const toTitleCase = (value: string) =>
    value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatStatus = (status: string, statusChanged: string | null) => {
    const label = toTitleCase(status || 'pending');
    return statusChanged ? `${label} at ${statusChanged}` : label;
};

const CreatorCell: React.FC<{ request: CollabDeletionRequest }> = ({ request }) => {
    const dispatch: AppDispatch = useDispatch();
    const name = request.influencer?.name || request.social_account?.handle || '-';
    const socialAccountId = request.social_account?.uuid;
    const influencerId = request.influencer?.uuid;

    if (!socialAccountId) {
        return <span>{name}</span>;
    }

    return (
        <button
            type="button"
            className="deletion-requests-link-btn"
            onClick={() =>
                dispatch(
                    toggleSocialAccountView({
                        socialAccountId,
                        influencerId: influencerId ?? undefined,
                    })
                )
            }
        >
            {name}
        </button>
    );
};

const DeletionRequestRow: React.FC<{ request: CollabDeletionRequest }> = ({ request }) => {
    const campaignName = request.campaign?.name || '-';
    const campaignId = request.campaign?.uuid;
    const collaborationId = request.collaboration?.uuid;
    const statusClass = (request.status || 'pending').toLowerCase();
    const showView = statusClass !== 'complete' && !!campaignId && !!collaborationId;

    return (
        <div className="deletion-request-row">
            <div className="deletion-request-cell" data-label="Campaign">
                {campaignId ? (
                    <Link
                        to={`/campaign/${campaignId}`}
                        className="deletion-requests-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {campaignName}
                    </Link>
                ) : (
                    <span>{campaignName}</span>
                )}
            </div>
            <div className="deletion-request-cell" data-label="Creator">
                <CreatorCell request={request} />
            </div>
            <div className="deletion-request-cell" data-label="Handle">
                {request.social_account?.handle ? (
                    <SocialAccountHandle
                        socialAccountId={request.social_account.uuid}
                        influencerId={request.influencer?.uuid}
                        handle={request.social_account.handle}
                        className="deletion-requests-handle"
                    />
                ) : (
                    <span>-</span>
                )}
            </div>
            <div className="deletion-request-cell" data-label="Status">
                <span className={`deletion-request-status deletion-request-status--${statusClass}`}>
                    {formatStatus(request.status, request.status_changed)}
                </span>
            </div>
           
            <div className="deletion-request-cell" data-label="Created By">
                {request.user?.name || '-'}
            </div>
            <div className="deletion-request-cell" data-label="Created At">
                {request.created || '-'}
            </div>
            <div className="deletion-request-cell" data-label="Remark">
                {request.remark?.trim() ? (
                    <span className="deletion-request-remark" title={request.remark}>
                        {request.remark}
                    </span>
                ) : (
                    <span>-</span>
                )}
            </div>
            <div className="deletion-request-cell deletion-request-cell--actions" data-label="Actions">
                {showView ? (
                    <Link
                        to={`/tasks/${campaignId}/collaboration/${collaborationId}`}
                        className="deletion-requests-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View
                    </Link>
                ) : (
                    <span>-</span>
                )}
            </div>
        </div>
    );
};

const SkeletonRows = ({ count }: { count: number }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <div className="deletion-request-row deletion-request-row--skeleton" key={`skeleton-${i}`}>
                {Array.from({ length: COLUMNS }).map((__, j) => (
                    <div className="deletion-request-cell" key={j}>
                        <Skeleton height={16} />
                    </div>
                ))}
            </div>
        ))}
    </>
);

const DeletionRequestsTable: React.FC<DeletionRequestsTableProps> = ({
    requests,
    hasMore,
    loading,
    initialLoading,
    error,
    onNextPage,
}) => {
    return (
        <InfiniteScroll
            dataLength={requests.length}
            next={onNextPage}
            hasMore={hasMore}
            loader={<></>}
            className="deletion-requests-scroll"
        >
            <div className="deletion-requests-table">
                <div className="deletion-request-row deletion-request-row--head">
                    <div className="deletion-request-cell">Campaign</div>
                    <div className="deletion-request-cell">Creator</div>
                    <div className="deletion-request-cell">Handle</div>
                    <div className="deletion-request-cell">Status</div>
                    <div className="deletion-request-cell">Created By</div>
                    <div className="deletion-request-cell">Created At</div>
                    <div className="deletion-request-cell">Remark</div>
                    <div className="deletion-request-cell deletion-request-cell--actions">Actions</div>
                </div>
                {!initialLoading &&
                    requests.map((request) => (
                        <DeletionRequestRow key={request.id} request={request} />
                    ))}
                {(initialLoading || (loading && requests.length > 0)) && (
                    <SkeletonRows count={initialLoading ? 8 : 3} />
                )}
            </div>
            {!(loading || initialLoading) && error && (
                <div className="deletion-requests-empty">
                    <button
                        type="button"
                        className="theme-btn theme-btn-sm btn-primary-outlined"
                        onClick={onNextPage}
                    >
                        Load More <i className="ms-2 fs-5 bx bx-refresh" />
                    </button>
                </div>
            )}
            {!(loading || initialLoading) && requests.length === 0 && !error && (
                <div className="deletion-requests-empty">
                    <h3>Currently there are no deletion requests in your list</h3>
                </div>
            )}
        </InfiniteScroll>
    );
};

export default DeletionRequestsTable;
